import React, { useEffect, useMemo, useState } from "react"
import { Dialog, DialogTitle, IconButton, makeStyles, Typography } from "@material-ui/core"
import { StorageFileViewModel } from "../../storage/api/storage-file"
import { useLazyGetStorageFileDownloadUrlQuery } from "../../storage/api"
import { Video, File, Maximize2, X, AlertCircle } from "react-feather"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import Skeleton from "@material-ui/lab/Skeleton"

type Props = {
  file: StorageFileViewModel
}

const PLACEHOLDER_FILE = { width: 180, height: 56 }

export const canPreviewVideo = (contentType: string) => {
  const video = document.createElement("video")
  return video.canPlayType(contentType) !== ""
}

const getFileTypeLabel = (contentType: string, t: TFunction) => {
  if (contentType.startsWith("video/")) return t("chatAttachment.fileTypeVideo")
  if (contentType.startsWith("image/")) return t("chatAttachment.fileTypeImage")
  if (contentType === "application/pdf") return t("chatAttachment.fileTypePdf")
  if (contentType.includes("zip")) return t("chatAttachment.fileTypeZipArchive")
  if (contentType.includes("spreadsheet") || contentType.includes("excel"))
    return t("chatAttachment.fileTypeSpreadsheet")
  if (contentType.includes("word")) return t("chatAttachment.fileTypeWordDocument")
  if (contentType.includes("octet-stream")) return t("chatAttachment.fileTypeBinary")
  return contentType
}

const ChatAttachment = ({ file }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")
  const { t: common } = useTranslation("translation")

  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [open, setOpen] = useState(false)

  const [getDownloadUrl] = useLazyGetStorageFileDownloadUrlQuery()

  useEffect(() => {
    let cancelled = false

    const loadUrl = async () => {
      try {
        const res = await getDownloadUrl(file.id).unwrap()

        if (!cancelled) {
          setUrl(res.url)
        }
      } catch {
        if (!cancelled) {
          setError(true)
        }
      }
    }

    loadUrl()

    return () => {
      cancelled = true
    }
  }, [file.id, getDownloadUrl])

  const isImage = file.contentType.startsWith("image/")
  const isVideo = file.contentType.startsWith("video/")

  const canPreview = useMemo(() => {
    if (!isVideo) return false

    return canPreviewVideo(file.contentType)
  }, [isVideo, file.contentType])

  if (error) {
    return (
      <div className={classes.fileCard}>
        <Typography className={classes.filename}>
          <AlertCircle size={16} className={classes.errorIcon} />
          {file.filename}
        </Typography>

        <Typography className={classes.fileType}>{t("chatAttachment.loadErrorMessage")}</Typography>
      </div>
    )
  }

  if (!url) {
    // Matches what it is about to become, so nothing below it moves when the real
    // attachment takes its place.
    const placeholder = isImage
      ? { width: 200, height: 120 }
      : canPreview
      ? { width: 300, height: 200 }
      : PLACEHOLDER_FILE

    return <Skeleton variant="rect" {...placeholder} style={{ borderRadius: 10 }} />
  }

  if (isImage) {
    return (
      <>
        <div className={classes.imageWrapper} onClick={() => setOpen(true)}>
          <img src={url} alt={file.filename} className={classes.image} />

          <div className={classes.imageOverlay}>
            <Maximize2 size={18} color="#fff" />
          </div>
        </div>

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" classes={{ paper: classes.dialogPaper }}>
          <div className={classes.dialogHeader}>
            <DialogTitle className={classes.dialogTitle}>{file.filename}</DialogTitle>

            <IconButton size="small" aria-label={common("close")} onClick={() => setOpen(false)}>
              <X size={18} />
            </IconButton>
          </div>

          <img
            src={url}
            alt={file.filename}
            style={{
              maxWidth: "90vw",
              maxHeight: "80vh",
              display: "block",
            }}
          />
        </Dialog>
      </>
    )
  }

  if (isVideo) {
    if (canPreview) {
      return (
        <video controls className={classes.video}>
          <source src={url} type={file.contentType} />
        </video>
      )
    }

    return (
      <div className={classes.fileCard}>
        <Typography component="a" href={url} target="_blank" rel="noopener noreferrer" className={classes.filenameLink}>
          <Video size={22} />

          <span className={classes.filenameText}>{file.filename}</span>
        </Typography>

        <Typography className={classes.fileType}>{getFileTypeLabel(file.contentType, t)}</Typography>
      </div>
    )
  }

  return (
    <div className={classes.fileCard}>
      <Typography component="a" href={url} target="_blank" rel="noopener noreferrer" className={classes.filenameLink}>
        <File size={18} />

        <span className={classes.filenameText}>{file.filename}</span>
      </Typography>

      <Typography className={classes.fileType}>{getFileTypeLabel(file.contentType, t)}</Typography>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  imageWrapper: {
    width: 200,
    height: 120,
    position: "relative",
    display: "inline-block",
    borderRadius: 10,
    overflow: "hidden",
    cursor: "pointer",
    lineHeight: 0,

    "&:hover $imageOverlay": {
      opacity: 1,
    },
  },

  image: {
    maxWidth: 200,
    maxHeight: 200,
    objectFit: "cover",
  },

  imageOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.25)",
    opacity: 0,
    transition: "opacity 0.15s ease",
  },

  dialogPaper: {
    borderRadius: 12,
    overflow: "hidden",
  },

  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(1),
  },

  dialogTitle: {
    fontSize: 14,
    fontWeight: 600,
  },

  // A fixed height, because a <video> is 150px tall until its metadata loads and then
  // resizes to its own aspect ratio — moving everything below it long after it rendered.
  video: {
    width: 300,
    height: 200,
    objectFit: "contain",
    borderRadius: 10,
    display: "block",
    background: "#000",
  },

  fileCard: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
    borderRadius: 5,
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[300]}`,
    minWidth: 180,
    overflow: "hidden",
    color: theme.palette.text.primary,

    "&:hover": {
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },
  },

  filenameLink: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    width: "100%",
    minWidth: 0,
    fontWeight: 600,
    color: "inherit",
    textDecoration: "none",

    "&:hover": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
    },
  },

  filenameText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
  },

  filename: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  fileType: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  errorIcon: {
    color: theme.palette.error.main,
    flexShrink: 0,
  },
}))

export default React.memo(ChatAttachment)
